import * as React from 'react';
import * as ReactDOMClient from "react-dom/client";
import analytics, { page, track, trackError } from './src/analytics';
import { useEffect, useRef } from 'react';
import type { GatsbyBrowser } from 'gatsby';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from './src/components/ErrorPage';
import safeStorage from './src/util/SafeStorage';
import { installNewRelicErrorHandler } from './src/util/newrelicErrorHandler';
import Intercom from '@intercom/messenger-js-sdk';
import { onLCP } from 'web-vitals';

export const replaceHydrateFunction = () => {
  return (element: React.ReactNode, container: Element, callback?: () => void) => {
    const root = ReactDOMClient.hydrateRoot(container, element, {
      onRecoverableError(error, info) {
        // Forward to your logger/telemetry
        const errorToLog = new Error("[Gatsby] recoverable hydration error: " + error.message, {
          cause: error,
        });
        errorToLog.stack = info.componentStack;
        error.componentStack = info.componentStack;
        // info.componentStack can be large; consider truncating
        console.error("[Gatsby] recoverable hydration error:", errorToLog);
      },
    });

    // Gatsby historically calls the callback after hydration
    if (typeof callback === "function") callback();

    // Returning the root is fine; Gatsby doesn't rely on the return value
    return root;
  };
};

function Wrapper({ children }: React.PropsWithChildren<{}>) {
  // Capture landing data on first page load
  useEffect(() => {
    // Check if already captured
    const existing = safeStorage.getItem('mw_landing');
    if (existing) return;

    // Parse UTM parameters
    const searchParams = new URLSearchParams(window.location.search);
    const utmParams: Record<string, string> = {};
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('utm_')) {
        utmParams[key] = value;
      }
    }

    // Build landing data object
    const landingData = {
      url: window.location.href,
      referrer: document.referrer || null,
      ...utmParams,
      capturedAt: new Date().toISOString(),
    };

    safeStorage.setItem('mw_landing', JSON.stringify(landingData));
  }, []);

  // Track first page interaction
  const trackedInteraciton = useRef(false);
  useEffect(() => {
    const handleInteraction = (event: MouseEvent | TouchEvent) => {
      if (!trackedInteraciton.current) {
        track('Page First Interaction', {
          path: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          eventType: event.type,
        });
        trackedInteraciton.current = true;
      }
    };
    const events = ['mousemove', 'click', 'touchstart'] as const;
    events.forEach(event => {
      document.addEventListener(event, handleInteraction);
    });
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, []);

  // Initialize Intercom on all pages
  useEffect(() => {
    let timeoutId = -1;
    let isMounted = true;

    Promise.race([
      new Promise((resolve) => {
        onLCP(resolve);
      }),
      new Promise((resolve) => {
        timeoutId = window.setTimeout(resolve, 8_000); // 8s
      }),
    ]).then(() => {
      // Only call Intercom if component is still mounted
      if (isMounted) {
        Intercom({
          app_id: 'sbcjnn32',
        });
      }
    });

    return () => {
      isMounted = false;
      if (timeoutId !== -1) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <ErrorPage title="Sorry an error occurred on this page" details={error.message} />
      )}
      onError={trackError}
    >
      {children}
    </ErrorBoundary>
  );
}

export const wrapRootElement = ({ element }) => {
  return <Wrapper>{element}</Wrapper>;
};
// On route update, set a timeout on page linger
// This keeps it in sync with https://github.com/DavidWells/analytics/blob/master/packages/gatsby-plugin-analytics/gatsby-browser.js
// which setups the page tracking
export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = (
  { location, prevLocation },
  options,
) => {
  const initPath = location.pathname;
  setTimeout(() => {
    const currentPath = window.location.pathname;
    if (currentPath === initPath) {
      const pageDataProperties = analytics.getState('page')?.last?.properties ?? null;
      track('Page Linger', {
        ...pageDataProperties,
      });
    }
  }, 1000 * 20);

  // Only fire when the page actually changes
  if (location.pathname !== prevLocation?.pathname) {
    page();
  }
};

if (typeof window !== 'undefined') {
  // New relic is set up first so we need to use the setErrorHandler to properly prevent
  // the error from being reported to new relic. The agent is loaded from js-agent.newrelic.com and
  // is commonly blocked by ad/tracker blockers, so this must be a no-op (never a throw) when
  // window.newrelic is missing: a throw here aborts the whole Gatsby client runtime (MW-13119).
  installNewRelicErrorHandler(window);
}
